package nl.volleyplanner.web.controller.thuisprogramma

import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.datetime.toLocalDateTime
import kotlinx.html.ButtonType
import kotlinx.html.FlowContent
import kotlinx.html.InputType
import kotlinx.html.TagConsumer
import kotlinx.html.button
import kotlinx.html.div
import kotlinx.html.h4
import kotlinx.html.id
import kotlinx.html.input
import kotlinx.html.js.onChangeFunction
import kotlinx.html.js.onClickFunction
import kotlinx.html.label
import kotlinx.html.p
import kotlinx.html.role
import kotlinx.html.span
import kotlinx.html.style
import nl.volleyplanner.api.planner.Task
import nl.volleyplanner.api.planner.TaskDaysResponse
import nl.volleyplanner.extensions.DEFAULT_TIMEZONE
import nl.volleyplanner.web.BackgroundJob
import nl.volleyplanner.web.BackgroundJobStatus
import nl.volleyplanner.web.BackgroundJobs
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.DATE_TIME_FORMAT
import nl.volleyplanner.web.DATE_TIME_SECOND_FORMAT
import nl.volleyplanner.web.DATE_TIME_TEXT_SHORT_NAMES
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component
import nl.volleyplanner.web.base.component.ListeningComponent
import nl.volleyplanner.web.base.component.checkbox
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.base.spinner
import nl.volleyplanner.web.controller.thuisprogramma.assignee_finder.TaskAssigneeStore
import nl.volleyplanner.web.lib.Alert
import nl.volleyplanner.web.textconverters.TaskLabelConverter
import nl.volleyplanner.web.util.combineAndLaunch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.asList


class ChangesBehindPublishedComponent(
    private val thuisprogrammaDataManager: ThuisprogrammaDataManager,
    val problemSlug: String,
    val problemVersionSlug: String?
) : HTMLComponent() {
    companion object {
        private val logger = Logger.getLogger("nl.volleyplanner.web.controller.thuisprogramma.ChangesBehindPublishedComponent")
    }

    val selectedProblemVersionData
        get() = thuisprogrammaDataManager.problemData.getVersionData(problemVersionSlug)
    val changesBehindPublishedValue
        get() = selectedProblemVersionData.changesBehindPublishedByTaskIdState.value
    val publishedTaskDaysResponseValue
        get() = thuisprogrammaDataManager.problemData.getPublishedVersionData().taskDaysState.value
    val versionTaskDaysResponseValue
        get() = selectedProblemVersionData.taskDaysState.value
    val mergePublishedJobState: StateFlow<BackgroundJob<MergeChangesBehindPublishedOperation>?>
        get() = selectedProblemVersionData.changesBehindPublishedOperationJob

    data class RenderModel(
        val mergePublishedJobState: BackgroundJob<MergeChangesBehindPublishedOperation>?,
        val changesBehindPublishedValue: Map<String, Task>?,
        val publishedTaskDaysResponseValue: TaskDaysResponse?,
        val publishedAssigneeStores: Map<String, TaskAssigneeStore>?,
        val versionTaskDaysResponseValue: TaskDaysResponse?
    )
    val selectedTaskIdsState = MutableStateFlow<Set<String>>(emptySet())

    fun FlowContent.renderChangesBehindPublishedOverview(renderModel: RenderModel) {
        val taskIds = renderModel.changesBehindPublishedValue!!.keys
        val matches = renderModel.publishedTaskDaysResponseValue!!.matchesByTaskId
            .filterKeys { taskIds.contains(it) }
            .values
            .toSet()
        // Given we are rerendering and setting all checkboxes to checked, update the state simply this way
        selectedTaskIdsState.value = taskIds

        div {
            checkbox(
                isChecked = true,
                label = "Alles selecteren",
                htmlId = "vp-changes-behind-select-all",
                onChangeFunction = { event ->
                    event.preventDefault()

                    val matchesElement = document.getElementById("vp-changes-behind-published-matches")!! as HTMLElement
                    if ((event.currentTarget as HTMLInputElement).checked) {
                        selectedTaskIdsState.value = taskIds
                        matchesElement.querySelectorAll(".vp-change-behind-checkbox")
                            .asList()
                            .forEach { (it as HTMLInputElement).checked = true }
                    } else {
                        selectedTaskIdsState.value = emptySet()
                        matchesElement.querySelectorAll(".vp-change-behind-checkbox")
                            .asList()
                            .forEach { (it as HTMLInputElement).checked = false }
                    }
                }
            )
        }

        div {
            id = "vp-changes-behind-published-matches"
            style = "height: 400px; min-height: 100px; overflow-y: auto; resize: vertical;"

            matches.sortedBy { it.match.startDateTime }
                .forEach { match ->
                    div(classes = "row rounded-2 shadow-sm mb-1") {
                        div(classes = "col col-lg-4 vp-text-bg-primary text-center py-1") {
                            // Match details
                            div {
                                +(match.match.startDateTime?.let { DATE_TIME_TEXT_SHORT_NAMES.format(it) } ?: "Nog niet gepland")
                            }
                            div {
                                +"${match.match.teamHome} - ${match.match.teamGuest}"
                            }
                        }
                        div(classes = "col col-lg-8 vp-text-bg-primary-secondary-children d-flex flex-column align-items-start justify-content-center") {
                            // Tasks
                            match.allTasks.filter { taskIds.contains(it.id) }
                                .forEach { task ->
                                    val assignmentStore = renderModel.publishedAssigneeStores!!
                                        .getValue(TaskAssigneeStore.getKey(match.match, task))
                                    div (classes = "text-start") {
                                        // Task details
                                        input(classes = "form-check-input vp-change-behind-checkbox") {
                                            type = InputType.checkBox
                                            id = "vp-change-behind-${task.id}"

                                            checked = true

                                            onChangeFunction = { event ->
                                                event.preventDefault()

                                                if ((event.currentTarget as HTMLInputElement).checked) {
                                                    selectedTaskIdsState.value += task.id
                                                } else {
                                                    selectedTaskIdsState.value -= task.id
                                                }

                                                val selectAllElement = document.getElementById("vp-changes-behind-select-all")!! as HTMLInputElement
                                                when (selectedTaskIdsState.value.size) {
                                                    taskIds.size -> {
                                                        selectAllElement.checked = true
                                                        selectAllElement.indeterminate = false
                                                    }
                                                    0 -> {
                                                        selectAllElement.checked = false
                                                        selectAllElement.indeterminate = false
                                                    }
                                                    else -> selectAllElement.indeterminate = true
                                                }
                                            }
                                        }
                                        label {
                                            htmlFor = "vp-change-behind-${task.id}"
                                            +"""
                                                            ${TaskLabelConverter.taskNameReadable(task)}: ${TaskLabelConverter.assigneeNameUIString(assignmentStore.task)} 
                                                            (toegewezen op ${DATE_TIME_SECOND_FORMAT.format(assignmentStore.task.assignee.assignedAtTimestamp)})
                                                        """.trimIndent()
                                        }
                                    }
                                }
                        }
                    }
                }
        }

        component(ListeningComponent("SaveSelectedChanges", listOf(selectedTaskIdsState)) {
            div (classes = "my-1") {
                div {
                    +"${selectedTaskIdsState.value.size} wijzigingen geselecteerd"
                }

                button(classes = "btn btn-primary") {
                    onClickFunction = { event ->
                        event.preventDefault()

                        thuisprogrammaDataManager.problemData.getSelectedVersionData()
                            .mergeChangesBehindPublished(selectedTaskIdsState.value)
                    }

                    +"Geselecteerde wijzigingen overnemen"
                }
                div {
                    span(classes = "small text-muted") { +"Van niet geselecteerde wijzigingen wordt uitgegaan dat de aanwijzing in deze versie de voorkeur geniet" }
                }
            }
        })
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val renderModel = RenderModel(
            mergePublishedJobState.value,
            changesBehindPublishedValue,
            publishedTaskDaysResponseValue,
            thuisprogrammaDataManager.problemData.getPublishedVersionData().assigneeStoresState.value,
            versionTaskDaysResponseValue
        )
        return consumer.div {
            if (renderModel.mergePublishedJobState != null) {
                val jobStatus = renderModel.mergePublishedJobState.jobStatus
                when (jobStatus) {
                    is BackgroundJobStatus.NotStarted -> {}
                    is BackgroundJobStatus.Running -> {
                        spinner()
                        +" verwerken van wijzigingen..."
                    }
                    is BackgroundJobStatus.Success -> {
                        div (classes = "alert alert-success alert-dismissible fade show") {
                            id = "vp-changes-behind-published-job-success-alert"
                            role = "alert"

                            icon("bi-check-lg")
                            +"${DATE_TIME_FORMAT.format(jobStatus.endTime.toLocalDateTime(DEFAULT_TIMEZONE))}: ${jobStatus.result!!.taskIdsToMerge.size} nieuwe wijzigingen overgenomen van de gepubliceerde versie"

                            button(classes = "btn-close") {
                                type = ButtonType.button
                                attributes["data-bs-dismiss"] = "alert"
                                attributes["aria-label"] = "Close"
                            }
                        }
                    }
                    is BackgroundJobStatus.Failure<*> -> {
                        div (classes = "alert alert-success alert-dismissible fade show") {
                            id = "vp-changes-behind-published-job-success-alert"
                            role = "alert"

                            icon("bi-cross")
                            +"${DATE_TIME_FORMAT.format(jobStatus.endTime.toLocalDateTime(DEFAULT_TIMEZONE))}: fout tijdens verwerken gepubliceerde wijzigingen. ${jobStatus.error?.message}"

                            button(classes = "btn-close") {
                                type = ButtonType.button
                                attributes["data-bs-dismiss"] = "alert"
                                attributes["aria-label"] = "Close"
                            }
                        }
                    }
                }
            }

            if (
                renderModel.changesBehindPublishedValue?.isNotEmpty() == true
                    && renderModel.publishedAssigneeStores != null
                    && renderModel.publishedTaskDaysResponseValue != null
                    && renderModel.versionTaskDaysResponseValue != null
                ) {

                div (classes = "container border border-danger-subtle rounded shadow-sm mb-2") {
                    div {
                        h4(classes = "mt-1") { +"Wijzigingen" }
                        val lastPublishedMergeTimestamp = renderModel.versionTaskDaysResponseValue.planningProblemVersion!!.lastPublishedMergeTimestamp
                        p {
                            +"""
                                Er zijn ${renderModel.changesBehindPublishedValue.size} wijzigingen in de gepubliceerde versie 
                                die nog niet zijn meegenomen in deze versie 
                                ${renderModel.versionTaskDaysResponseValue.planningProblemVersion!!.name} 
                                (sinds ${DATE_TIME_SECOND_FORMAT.format(lastPublishedMergeTimestamp)}).
                            """.trimIndent()
                        }
                    }

                    // Als er geen job is: wijzigingen tonen
                    // Als job klaar is, en er zijn wijzigingen: tonen
                    // Als job bezig is: spinner tonen

                    if (renderModel.mergePublishedJobState != null) {
                        spinner()
                        +" verwerken van wijzigingen..."
                    } else {
                        renderChangesBehindPublishedOverview(renderModel)
                    }
                }
            }
        }
    }

    override suspend fun getState() = listOf(
        thuisprogrammaDataManager.problemData.getSelectedVersionData().changesBehindPublishedByTaskIdState.subscribe(),
        thuisprogrammaDataManager.problemData.getPublishedVersionData().assigneeStoresState,
        thuisprogrammaDataManager.problemData.getSelectedVersionData().taskDaysState.subscribe(),
        thuisprogrammaDataManager.problemData.getSelectedVersionData().changesBehindPublishedOperationJob
    ).combineAndLaunch(CoroutineManager.RENDER_SCOPE)
}